import { Injectable, inject } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private swPush = inject(SwPush);
  private http = inject(HttpClient);
  private router = inject(Router);
  private config = inject(ConfigService);
  private subscribed = false;
  private clickHandlerSetup = false;

  async subscribeToPush(): Promise<boolean> {
    if (this.subscribed) return true;
    if (!this.swPush.isEnabled) {
      console.warn('Service Worker Push not available');
      return false;
    }

    try {
      const baseUrl = this.config.getApiUrl();
      const vapidRes = await this.http.get<{ success: boolean; publicKey: string }>(
        `${baseUrl}/api/v1/push/vapid-key`
      ).toPromise();

      if (!vapidRes?.publicKey) {
        console.warn('No VAPID public key from server');
        return false;
      }

      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: vapidRes.publicKey
      });

      const subJson = subscription.toJSON();
      await this.http.post(`${baseUrl}/api/v1/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys?.['p256dh'] || '',
          auth: subJson.keys?.['auth'] || ''
        }
      }).toPromise();

      this.subscribed = true;
      console.log('Push notification subscription registered');
      return true;
    } catch (err) {
      console.error('Failed to subscribe to push:', err);
      return false;
    }
  }

  async unsubscribeFromPush(): Promise<void> {
    if (!this.swPush.isEnabled) return;

    try {
      const sub = await (navigator.serviceWorker?.ready)
        ?.then(reg => reg.pushManager.getSubscription());

      if (sub) {
        const baseUrl = this.config.getApiUrl();
        await this.http.delete(`${baseUrl}/api/v1/push/unsubscribe`, {
          body: { endpoint: sub.endpoint }
        }).toPromise();
        await sub.unsubscribe();
      }

      this.subscribed = false;
    } catch (err) {
      console.error('Failed to unsubscribe from push:', err);
    }
  }

  /**
   * Handle notification clicks.
   * ngsw broadcasts NOTIFICATION_CLICK to all clients after handling the click.
   * SwPush.notificationClicks wraps this broadcast — we use it to navigate.
   *
   * For the case when the app is in background and gets focused by ngsw's
   * navigateLastFocusedOrOpen, the client.navigate() already handles navigation.
   * But on browsers that don't support WindowClient.navigate() (iOS Safari),
   * we fall back to Angular Router navigation via this handler.
   */
  setupClickHandler(): void {
    if (this.clickHandlerSetup) return;
    this.clickHandlerSetup = true;

    // SwPush.notificationClicks fires when ngsw broadcasts NOTIFICATION_CLICK
    this.swPush.notificationClicks.subscribe(event => {
      console.log('Notification clicked:', event);

      // Extract URL from the notification data
      const notifData = event.notification?.data;
      const url = notifData?.onActionClick?.default?.url
        || notifData?.url
        || '/order-history';

      console.log('Navigating to:', url);
      this.router.navigateByUrl(url);
    });
  }
}
